import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useSession } from "../auth/Session";
import { ErreurApi } from "../api/client";

/** Doit rester égal à `IdentifiantsPortail.LongueurMinimale` côté API. */
const LONGUEUR_MINIMALE = 12;

/**
 * §5.A — Le lien d'invitation, à USAGE UNIQUE.
 *
 * Il n'ouvre plus de session : il ne sert qu'à établir des identifiants. Une
 * fois le compte créé, le lien est consommé et la connexion se fait au mot de
 * passe, y compris à chaque expiration de session.
 */
export function CreationCompte() {
  const { jeton } = useParams();
  const { session, verifierLien, creerCompte } = useSession();
  const naviguer = useNavigate();

  const [nomDossier, setNomDossier] = useState<string | null>(null);
  const [etat, setEtat] =
    useState<"verification" | "formulaire" | "refuse" | "dejaCree">("verification");
  const dejaConnecte = session !== null && (session.role === "client" || session.role === "delegue");

  const [identifiant, setIdentifiant] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    // Une session ouverte rend le lien sans objet : on ne va pas demander a
    // quelqu'un qui est DEJA DANS son dossier de recreer ses acces.
    if (!jeton || dejaConnecte) return;
    verifierLien(jeton)
      .then((r) => {
        setNomDossier(r.nom);
        // Reemettre un lien redonne le CHEMIN du portail, pas de nouveaux
        // identifiants. Des acces deja etablis menent donc a la connexion.
        setEtat(r.etat === "DejaCree" ? "dejaCree" : "formulaire");
      })
      .catch(() => {
        // Le motif du serveur n'est pas affiche, et c'est deliberé : il ne
        // distingue pas un lien inconnu d'un lien deja consomme — precisement
        // pour ne rien apprendre a qui essaie des jetons au hasard.
        setEtat("refuse");
      });
  }, [jeton, verifierLien, dejaConnecte]);

  // ⛔ CAS 1 — DEJA CONNECTE : le lien ne sert plus a rien, on rentre.
  if (dejaConnecte) return <Navigate to="/portail" replace />;

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);

    // ⛔ La confirmation est vérifiée ICI et nulle part ailleurs.
    // Le serveur ne la reçoit pas : elle ne protège pas contre un attaquant,
    // elle protège contre une faute de frappe. Envoyée au serveur, elle
    // n'apporterait rien et ferait un secret de plus à transporter.
    if (motDePasse !== confirmation) {
      setErreur("Les deux mots de passe ne sont pas identiques.");
      return;
    }

    if (!jeton) return;
    setEnCours(true);
    try {
      await creerCompte(jeton, identifiant, motDePasse);
      naviguer("/portail", { replace: true });
    } catch (e) {
      // Le serveur rend un motif lisible — identifiant déjà pris, mot de passe
      // trop court. On l'affiche tel quel : le reformuler le rendrait vague.
      setErreur(
        e instanceof ErreurApi
          ? [e.message, e.motif].filter(Boolean).join(" ")
          : "Création impossible.",
      );
    } finally {
      setEnCours(false);
    }
  }

  if (etat === "verification") {
    return (
      <main className="connexion">
        <h1>Portail client</h1>
        <p>Vérification de votre lien…</p>
      </main>
    );
  }

  // ⛔ CAS 2 — ACCES DEJA ETABLIS : le lien est valable, mais il n'y a plus rien
  // a creer. On mene a la connexion.
  if (etat === "dejaCree") {
    return (
      <Navigate
        to="/portail/connexion"
        replace
        state={{ avis: "Vos accès existent déjà. Connectez-vous avec vos identifiants." }}
      />
    );
  }

  // ⛔ CAS 3 — LIEN CONSOMME OU INCONNU : on envoie a la CONNEXION, pas sur une
  // page d'erreur.
  //
  // Le cas de loin le plus courant est le client qui a deja ses identifiants et
  // reouvre l'ancien courriel. Lui presenter un bandeau rouge lui ferait croire
  // que son compte est casse, et il appellerait le cabinet pour rien. L'ecran de
  // connexion, lui, est exactement ce dont il a besoin.
  //
  // Le renvoi est le MEME pour un jeton qui n'a jamais existe : cet ecran ne
  // dit donc rien de plus que la reponse du serveur, qui confond deja les deux.
  if (etat === "refuse") {
    return (
      <Navigate
        to="/portail/connexion"
        replace
        state={{ avis: "Ce lien a déjà servi. Connectez-vous avec vos identifiants." }}
      />
    );
  }

  return (
    <main className="connexion">
      <header className="marque">
        <div className="marque__monogramme">MDC</div>
        <div className="marque__nom">La Maison de la Conformité</div>
        <p className="marque__sous-titre">
          {nomDossier ?? "Votre dossier"} · Création de vos accès
        </p>
      </header>

      <p className="resume" style={{ textAlign: "center", marginBottom: "1.2rem" }}>
        Ce lien ne fonctionne <strong>qu'une seule fois</strong>. Choisissez vos
        identifiants : ils vous serviront à chaque connexion.
      </p>

      <form onSubmit={soumettre}>
        <label>
          Identifiant
          <input
            value={identifiant}
            onChange={(e) => setIdentifiant(e.target.value)}
            autoComplete="username"
            placeholder="Ex. globe.ci"
            minLength={3}
            required
          />
        </label>
        <label>
          Mot de passe
          <input
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="new-password"
            minLength={LONGUEUR_MINIMALE}
            required
          />
        </label>
        <label>
          Confirmez le mot de passe
          <input
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            minLength={LONGUEUR_MINIMALE}
            required
          />
        </label>

        <p className="note">
          Au moins {LONGUEUR_MINIMALE} caractères. Une phrase de trois mots
          convient parfaitement, et se retient mieux qu'un mot compliqué.
        </p>

        {erreur && (
          <p className="alerte" role="alert">
            {erreur}
          </p>
        )}

        <button type="submit" disabled={enCours}>
          {enCours ? "Création…" : "Créer mes accès"}
        </button>
      </form>
    </main>
  );
}
